import {
  BY_HOURS_LABEL,
  NOT_CREDITED_LABEL,
  NOT_CREDITED_REASON_LABEL,
  SPLIT_EVENLY_LABEL,
  sharePct,
  type RevenueByPerson,
  type RevenueNotCredited,
  type RevenuePersonMix,
  type RevenuePersonRow,
  type WithheldField,
} from '@servwell/shared';
import { cn } from '@/lib/utils';
import { Notice } from '../../ui/Badge';
import { Change } from '../../ui/Change';
import { FigureButton } from '../../ui/Button';
import { money } from '../../money';
import { Mark } from './Mark';
import { opensInPeriod } from './RevenueDrill';
import type { BasisWords } from './basisWords';
import { REVENUE_SECTION, byPersonReasons, showsNotCredited } from './onScreen';

/**
 * Revenue by person (ops insights Phase 2, ticket 03): how the period's revenue was credited,
 * rule by rule, then each person largest first, then "Not credited", last. The server decided
 * every figure (`byPerson`), and the people with "Not credited" add up to the headline to the
 * cent, in the period and in the same days a year earlier; this draws them. Each row carries
 * its change against a year earlier, and someone who earned only a year earlier keeps a row.
 *
 * A list, not a table: each row wraps inside its own width (names break anywhere, figures
 * flow onto the next line), so the section fits a phone and half the page at `lg` without ever
 * scrolling sideways. The mix bar is decoration: every kind of work in it is named in text,
 * with its amount, beside it.
 *
 * Each row carries `data-row` ("person" or "not-credited"), so a test can find a row by what it
 * is rather than by how it is styled.
 *
 * A person's name and amount, and "Not credited"'s, open the invoices behind them (ticket 06):
 * a button around that line alone, so the change against a year earlier stays outside it. A row
 * with nothing in the period (someone credited only a year earlier) opens nothing.
 */

const HEADING_ID = 'revenue-by-person';

function SectionHeading() {
  return (
    <h2 id={HEADING_ID} className="font-heading text-sm font-semibold">
      {REVENUE_SECTION.byPerson}
    </h2>
  );
}

export function ByPerson({
  byPerson,
  words,
  days,
  onOpenPerson,
  onOpenNotCredited,
}: {
  byPerson: RevenueByPerson;
  /** The basis the people are credited on, in words: their jobs and their average. */
  words: BasisWords;
  days: string;
  /** A person was opened: the invoices they are credited on, in the drill. */
  onOpenPerson: (row: RevenuePersonRow) => void;
  /** "Not credited" was opened. */
  onOpenNotCredited: () => void;
}) {
  const { rows, notCredited, ruleTotals } = byPerson;
  const showNotCredited = showsNotCredited(notCredited);
  return (
    <section aria-labelledby={HEADING_ID} className="min-w-0 space-y-2">
      <SectionHeading />
      <dl className="flex flex-wrap gap-x-6 gap-y-2" aria-label="How it was credited">
        <RuleTotal label={BY_HOURS_LABEL} cents={ruleTotals.byHoursCents} />
        <RuleTotal label={SPLIT_EVENLY_LABEL} cents={ruleTotals.splitEvenlyCents} />
        {/* The third rule total is the "Not credited" row's own amount. */}
        <RuleTotal label={NOT_CREDITED_LABEL} cents={notCredited.cents} />
      </dl>
      {rows.length === 0 && !showNotCredited ? (
        <p className="text-sm text-muted-foreground">Nobody was credited in {days}.</p>
      ) : (
        <ul className="min-w-0 divide-y divide-border rounded-[var(--radius)] border border-border bg-surface">
          {rows.map((row) => (
            <PersonRow
              key={row.userId}
              row={row}
              words={words}
              onOpen={() => onOpenPerson(row)}
            />
          ))}
          {showNotCredited ? (
            <NotCreditedRow notCredited={notCredited} onOpen={onOpenNotCredited} />
          ) : null}
        </ul>
      )}
    </section>
  );
}

/**
 * Where revenue by person would be, when it is not drawn (`byPersonShown`): its heading and the
 * server's own reason for THIS section (`byPersonReasons`; another field's reason belongs beside
 * that field). Drawn from `withheld`, never from the caller's role, so the page says exactly
 * what the server decided.
 */
export function ByPersonWithheld({ withheld }: { withheld: WithheldField[] }) {
  return (
    <section aria-labelledby={HEADING_ID} className="min-w-0 space-y-2">
      <SectionHeading />
      <Notice tone="info">
        {byPersonReasons(withheld).map((w) => (
          <p key={w.field}>{w.reason}</p>
        ))}
      </Notice>
    </section>
  );
}

function RuleTotal({ label, cents }: { label: string; cents: number }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-semibold tabular-nums">{money(cents)}</dd>
    </div>
  );
}

/**
 * A name and an amount on one line; the amount keeps its width, the name wraps. When `onOpen`
 * is given, the line is the button that opens its invoices (spans throughout: a button holds
 * only phrasing content).
 */
function Line({
  name,
  cents,
  muted,
  onOpen,
}: {
  name: string;
  cents: number;
  muted?: boolean;
  onOpen?: () => void;
}) {
  const line = (
    <span className="flex items-baseline justify-between gap-3">
      <span
        className={cn(
          'min-w-0 font-medium wrap-anywhere',
          muted && 'text-muted-foreground',
        )}
      >
        {name}
      </span>
      <span className="shrink-0 font-semibold tabular-nums">{money(cents)}</span>
    </span>
  );
  if (!onOpen) return line;
  return <FigureButton onClick={onOpen}>{line}</FigureButton>;
}

/**
 * One person, and under their amount its change against the same days a year earlier ("−100.0%
 * vs a year earlier"; the days themselves are on the headline figures). Someone with a share
 * only a year earlier reads 0 now, "0 jobs", no average ("—") and no mix.
 */
function PersonRow({
  row,
  words,
  onOpen,
}: {
  row: RevenuePersonRow;
  words: BasisWords;
  onOpen: () => void;
}) {
  return (
    <li data-row="person" className="min-w-0 space-y-1.5 px-4 py-3">
      {/* Someone there only a year earlier has nothing in the period to open. */}
      <Line
        name={row.name}
        cents={row.cents}
        onOpen={opensInPeriod(row.cents, row.jobs) ? onOpen : undefined}
      />
      <p className="text-xs text-muted-foreground">
        <Change
          now={row.cents}
          yearEarlier={row.yearEarlierCents}
          against="a year earlier"
        />
      </p>
      <p className="text-xs text-muted-foreground">
        {words.jobCount(row.jobs)} · {words.averageJob.toLowerCase()}{' '}
        {money(row.averageJobCents)}
      </p>
      <p className="text-xs text-muted-foreground">
        {BY_HOURS_LABEL} {money(row.byHoursCents)} · {SPLIT_EVENLY_LABEL}{' '}
        {money(row.splitEvenlyCents)}
      </p>
      <Mix mix={row.mix} />
    </li>
  );
}

/**
 * A person's revenue by kind of work: a bar of their positive amounts, in each type's colour
 * ("No service type" hatched), and the same kinds named with their amounts. The bar is hidden
 * from screen readers; the names are what carry it.
 */
function Mix({ mix }: { mix: RevenuePersonMix[] }) {
  const drawn = mix.filter((m) => m.cents > 0);
  const total = drawn.reduce((n, m) => n + m.cents, 0);
  return (
    <div className="space-y-1">
      {total > 0 ? (
        <span
          aria-hidden="true"
          className="flex h-2 gap-px overflow-hidden rounded-full bg-surface-2"
        >
          {drawn.map((m) => (
            <Mark
              key={m.key}
              of={m}
              className="h-full"
              width={`${sharePct(m.cents, total, 2)}%`}
            />
          ))}
        </span>
      ) : null}
      <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        {mix.map((m) => (
          <li key={m.key} className="inline-flex min-w-0 items-center gap-1.5">
            <Mark of={m} className="size-2 shrink-0 rounded-full" />
            <span className="min-w-0 wrap-anywhere">{m.label}</span>
            <span className="tabular-nums">{money(m.cents)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** What nobody could be credited with, and its two reasons, each with its amount. */
function NotCreditedRow({
  notCredited,
  onOpen,
}: {
  notCredited: RevenueNotCredited;
  onOpen: () => void;
}) {
  return (
    <li data-row="not-credited" className="min-w-0 space-y-1.5 px-4 py-3">
      {/* It opens when it holds anything in the period: an amount, or invoices (payments and
          refunds) that came to nothing. Shown for a year earlier alone, it has nothing to
          open. */}
      <Line
        name={NOT_CREDITED_LABEL}
        cents={notCredited.cents}
        muted
        onOpen={opensInPeriod(notCredited.cents, notCredited.count) ? onOpen : undefined}
      />
      <p className="text-xs text-muted-foreground">
        <Change
          now={notCredited.cents}
          yearEarlier={notCredited.yearEarlierCents}
          against="a year earlier"
        />
      </p>
      <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        <li className="inline-flex gap-1.5">
          <span>{NOT_CREDITED_REASON_LABEL['not-tied']}</span>
          <span className="tabular-nums">{money(notCredited.notTiedCents)}</span>
        </li>
        <li className="inline-flex gap-1.5">
          <span>{NOT_CREDITED_REASON_LABEL['no-hours-or-assignees']}</span>
          <span className="tabular-nums">
            {money(notCredited.noHoursOrAssigneesCents)}
          </span>
        </li>
      </ul>
    </li>
  );
}
